<section class="figma-settings" aria-label="Valg av stylingoppsett">
  <div class="mode-row" [formGroup]="draftForm">
    <div>
      <span class="eyebrow">Konfigurasjonsmåte</span>
      <mat-button-toggle-group formControlName="mode" aria-label="Konfigurasjonsmåte">
        <mat-button-toggle value="standard">Standard</mat-button-toggle>
        <mat-button-toggle value="experimental">Figma · utkast</mat-button-toggle>
      </mat-button-toggle-group>
    </div>
    @if (loaded) {
      <button mat-stroked-button type="button" (click)="saveDraft()"
        [disabled]="savingDraft || refreshing || draftForm.invalid || !draftForm.dirty">
        {{ savingDraft ? 'Lagrer…' : 'Lagre Figma-valg' }}
      </button>
    }
  </div>
  @if (loading) { <p role="status">Laster konfigurasjonsvalg…</p> }
  @if (!clientId || !chatbotId) { <p class="muted">Figma-oppsettet blir tilgjengelig når chatboten er lagret.</p> }
  @if (error) {
    <div class="error" role="alert">
      {{ error }}
      <button mat-button type="button" (click)="load()" [disabled]="loading || savingDraft || savingMaster || refreshing">Last inn på nytt</button>
    </div>
  }
  @if (status) { <p class="status" role="status">{{ status }}</p> }

  @if (experimental) {
    <div class="experimental-content">
      <div class="intro">
        <span class="badge">{{ localRuntimeEnabled ? 'Figma · Testing' : 'Figma · Forhåndsvisning' }}</span>
        <h3>Hent chatdesignet fra Figma</h3>
        <p>Hent farger, typografi, avstander og form fra designet. Navn, knappetekster og data styres fortsatt av Backoffice.
          @if (localRuntimeEnabled) { Lagret valg og Refresh design oppdaterer chatten automatisk. }
          @else { Designet publiseres ikke når du trykker Refresh design. }</p>
        <p class="muted">Figma-valget lagres separat fra chatbotens vanlige lagring og publisering.</p>
      </div>

      <div class="customer-section" [formGroup]="draftForm">
        <mat-form-field appearance="outline">
          <mat-label>Din tilpassede Figma-side</mat-label>
          <input matInput formControlName="designUrl" type="url" [readonly]="savingDraft"
            placeholder="https://www.figma.com/design/…?node-id=…">
          @if (draftForm.controls.designUrl.invalid) {
            <mat-error>Bruk en HTTPS-lenke til en Figma-side med én node-id.</mat-error>
          }
        </mat-form-field>
        <p class="help">Finn URL-en i Figma ved å høyreklikke på siden (page) og velge <strong>Copy link to page</strong>.</p>
        <p>Bruk siden med navnekontrakten <code>shine-chat-v1</code>. Behold skjermene
          <code>ChatWindow/Desktop/Start</code>, <code>ChatWindow/Desktop/Conversation</code> og tilsvarende for <code>Mobile</code>.
          Se «Forslag til lagnavn» i skissen.</p>
        <button mat-flat-button color="primary" class="refresh-design-button" type="button" (click)="refreshDesign()"
          [disabled]="!canRefresh || refreshing || savingDraft || draftForm.invalid || !draftForm.controls.designUrl.value">
          <mat-icon aria-hidden="true">refresh</mat-icon>
          {{ refreshing ? 'Henter og validerer…' : 'Refresh design' }}
        </button>
        @if (refreshing) { <mat-progress-bar mode="indeterminate" aria-label="Henter og validerer Figma-design"></mat-progress-bar> }
        @if (!canRefresh) { <p class="muted">Import krever administrator og at Figma er aktivert i dette miljøet.</p> }
        @if (canRefresh && !connected) { <p class="muted">Shine-serverens Figma-tilkobling må konfigureres før import. Codex-tilgangen er separat.</p> }
      </div>

      <section aria-label="Forhåndsvisning og varianter" class="preview-section">
        <h4>Forhåndsvisning</h4>
        @if (snapshot) {
          <p>Sist hentet: {{ snapshot.importedAt | date:'dd.MM.yyyy HH:mm' }} · Kildeversjon {{ snapshot.sourceVersion }}</p>
          @if (snapshot.sourceUrl !== draftForm.controls.designUrl.value) {
            <p class="error">Forhåndsvisningen tilhører den forrige Figma-lenken. Hent designet på nytt for å oppdatere.</p>
          }
          <mat-button-toggle-group [value]="selectedView" (change)="selectView($event.value)" aria-label="Velg forhåndsvisning">
            @for (view of snapshot.views; track view.name) { <mat-button-toggle [value]="view.name">{{ view.name }}</mat-button-toggle> }
          </mat-button-toggle-group>
          @if (selectedView.startsWith('Desktop/')) {
            <p class="muted">Desktop vises i skissens fulle bredde. Rull sideveis for å se hele visningen.</p>
          }
          <div class="preview-scroll">
            <div class="design-preview-viewport" tabindex="0" role="region" aria-label="Designforhåndsvisning – kan rulles vannrett">
            <iframe #preview [src]="previewUrl" title="Figma-design i lokal chatforhåndsvisning"
              [style.width.px]="currentView?.elements?.['window']?.style?.width || 390"
              [style.height.px]="currentView?.elements?.['window']?.style?.height || 844"
              sandbox="allow-scripts allow-same-origin" (load)="onPreviewLoad(preview)"></iframe>
            </div>
          </div>
          <p role="status">{{ previewStatus }}</p>
          @if (!snapshot.preview) { <p class="muted">Bruk Refresh design for å hente bilder, logo og eksempeltekster til forhåndsvisningen.</p> }
          <p class="muted">Bilder, logo og eksempeltekster er hentet fra Figma. Visningen bruker chatens komponenter og stilregler.
            Fontene må finnes som webfonter eller lokalt. Chatten bruker fortsatt sine egne tekster og produkter. Ingen meldinger sendes her.</p>
          <details [open]="snapshot.diagnostics.length > 0">
            <summary>Merknader fra importen ({{ snapshot.diagnostics.length }})</summary>
            @for (item of snapshot.diagnostics; track $index) {
              <p><strong>{{ item.message }}</strong><br><code>{{ item.path }}</code></p>
            } @empty { <p>Ingen avvik fra de støttede egenskapene.</p> }
          </details>
        } @else {
          <div class="preview-empty">
          <mat-icon aria-hidden="true">image_search</mat-icon>
          <strong>Ingen forhåndsvisning ennå</strong>
          <span>Legg inn Figma-lenken og trykk Refresh design.</span>
          </div>
        }
      </section>

      @if (canManageMaster) {
        <div class="master-section" [formGroup]="masterForm" data-testid="figma-master">
          <span class="eyebrow">Kun administrator · Felles for alle chatboter</span>
          <p class="muted">Felles komponentbibliotek. Refresh design henter refererte hovedkomponenter og tilstandsvarianter fra alle sider i denne Figma-filen. Designlenken over velger fortsatt chatbotens egen side.</p>
          <mat-form-field appearance="outline">
            <mat-label>Figma masterfil</mat-label>
            <input matInput formControlName="masterTemplateUrl" type="url" [readonly]="savingMaster"
              placeholder="https://www.figma.com/design/…/Master">
            @if (masterForm.controls.masterTemplateUrl.invalid) {
              <mat-error>Bruk en HTTPS-lenke til Figma-filen. Sidelenke eller Dev Mode-lenke kan også brukes.</mat-error>
            }
          </mat-form-field>
          <p class="muted">En eventuell side/node i masterlenken begrenser ikke hvilke sider komponentene kan hentes fra.</p>
          @if (!masterForm.controls.masterTemplateUrl.value) {
            <p class="muted">Ingen master er konfigurert ennå. Du kan lagre den når malen er klar.</p>
          }
          <button mat-stroked-button type="button" (click)="saveMaster()"
            [disabled]="savingMaster || masterForm.invalid || !masterForm.dirty">
            {{ savingMaster ? 'Lagrer…' : 'Lagre master' }}
          </button>
        </div>
      }

    </div>
  }
</section>
